// listas-20261022: filtro de llamadas del contestador (desactivado / lista blanca / lista negra)
import { useCallback, useEffect, useState } from 'react';
import { PhoneOff } from 'lucide-react';
import { toast } from 'sonner';
import { getPizasCallScreening, updatePizasCallScreening } from '../lib/api';
import { entriesFromApi } from '../lib/waBlacklistUtils';
import PhoneListEditor from './PhoneListEditor';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

const MODOS = [
  { id: 'off', label: 'Desactivado', help: 'El contestador atiende todas las llamadas.' },
  { id: 'whitelist', label: 'Lista blanca', help: 'Solo atiende a los números de la lista blanca; los demás se rechazan.' },
  { id: 'blacklist', label: 'Lista negra', help: 'Atiende a todos excepto a los números de la lista negra.' },
];

export default function CallScreeningCard() {
  const [cfg, setCfg] = useState({ mode: 'off', whitelist: [], blacklist: [] });
  const [loading, setLoading] = useState(true);

  const apply = (d) => setCfg({
    mode: d?.mode || 'off',
    whitelist: entriesFromApi({ entries: d?.whitelist || [] }),
    blacklist: entriesFromApi({ entries: d?.blacklist || [] }),
  });
  const load = useCallback(async () => {
    try {
      apply(await getPizasCallScreening());
    } catch {
      toast.error('No se pudo cargar el filtro de llamadas.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setMode = async (mode) => {
    if (mode === 'whitelist' && cfg.whitelist.length === 0) {
      toast.warning('Lista blanca vacía: se rechazarán todas las llamadas (excepto la línea del negocio).');
    }
    try {
      apply(await updatePizasCallScreening({ mode }));
      toast.success(`Filtro de llamadas: ${MODOS.find((m) => m.id === mode)?.label}`);
    } catch {
      toast.error('No se pudo guardar.');
    }
  };
  const lista = cfg.mode === 'blacklist' ? 'blacklist' : 'whitelist';

  return (
    <Card className="max-w-xl border border-border" data-testid="call-screening-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <PhoneOff className="w-5 h-5 text-primary" />
          Llamadas del contestador
        </CardTitle>
        <CardDescription>
          Las llamadas rechazadas no se contestan (sin costo de voz). Los cambios se aplican en menos de 30 s.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <p className="text-sm text-muted-foreground animate-pulse">Cargando…</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Modo del filtro">
              {MODOS.map((m) => (
                <Button key={m.id} type="button" role="radio" aria-checked={cfg.mode === m.id}
                  variant={cfg.mode === m.id ? 'default' : 'outline'} onClick={() => setMode(m.id)}
                  data-testid={`call-mode-${m.id}`}>
                  {m.label}
                </Button>
              ))}
            </div>
            <p className="text-sm text-muted-foreground">{MODOS.find((m) => m.id === cfg.mode)?.help}</p>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" role="note">
              Si las llamadas llegan <strong>desviadas desde la línea del negocio</strong>, algunos operadores
              muestran el número del negocio en lugar del cliente. La línea del negocio (la del WhatsApp vinculado y
              las de VOICE_FORWARDING_NUMBERS) <strong>nunca se bloquea</strong>; en ese caso el filtro no puede
              distinguir al cliente.
            </div>
            {cfg.mode !== 'off' ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">{lista === 'blacklist' ? 'Lista negra' : 'Lista blanca'}</p>
                <PhoneListEditor
                  idPrefix={`call_${lista}`}
                  entries={cfg[lista]}
                  emptyText={lista === 'blacklist' ? 'Sin números bloqueados.' : 'Sin números permitidos.'}
                  onChange={async (next) => apply(await updatePizasCallScreening({ [lista]: next }))}
                />
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
